import React, {
    useContext,
    useEffect,
    useMemo,
    useState
} from 'react';

import {
    Alert,
    Button,
    Col,
    ControlLabel,
    FormControl,
    FormGroup,
    Modal,
    Row
} from 'react-bootstrap';

import {ApiContext} from '../../../api/Api';


const collection = response => {
    if (!response)
        return [];

    if (Array.isArray(response))
        return response;

    if (Array.isArray(response.data))
        return response.data;

    if (Array.isArray(response['hydra:member']))
        return response['hydra:member'];

    if (
        response.data &&
        Array.isArray(response.data['hydra:member'])
    )
        return response.data['hydra:member'];

    return [];
};


const data = response =>
    response && response.data
        ? response.data
        : response || {};


const relationId = relation => {
    if (!relation)
        return '';

    if (typeof relation === 'object' && relation.id)
        return String(relation.id);

    const value =
        typeof relation === 'object'
            ? relation['@id']
            : relation;

    if (!value)
        return '';

    return String(value).split('/').filter(Boolean).pop() || '';
};


const datePart = value => {
    if (!value)
        return '';

    const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);

    return match ? match[1] : '';
};


const timePart = value => {
    if (!value)
        return '';

    const match = String(value).match(/T(\d{2}:\d{2})/);

    if (match)
        return match[1];

    const date = new Date(value);

    if (Number.isNaN(date.getTime()))
        return '';

    return [
        String(date.getHours()).padStart(2, '0'),
        String(date.getMinutes()).padStart(2, '0')
    ].join(':');
};


const slotValue = slot => {
    if (!slot)
        return '';

    if (typeof slot === 'string')
        return slot;

    return (
        slot.start ||
        slot.scheduledStartDate ||
        slot.dateTime ||
        slot.datetime ||
        slot.value ||
        ''
    );
};


const slotLabel = slot => {
    const value = slotValue(slot);

    if (/^\d{2}:\d{2}/.test(value))
        return value.substring(0, 5);

    const match = String(value).match(/T(\d{2}:\d{2})/);

    return match ? match[1] : value;
};


const appointmentDateTime = (slot, date) => {
    const value = slotValue(slot);

    if (/^\d{2}:\d{2}$/.test(value))
        return `${date}T${value}:00`;

    if (/^\d{2}:\d{2}:\d{2}$/.test(value))
        return `${date}T${value}`;

    return value;
};


const staffLabel = staff => {
    const employee = staff.employee || {};

    if (typeof employee === 'string')
        return `Colaborador ${relationId(employee)}`;

    return (
        employee.fullName ||
        [
            employee.name,
            employee.patLastName,
            employee.matLastName
        ].filter(Boolean).join(' ') ||
        staff.name ||
        `Colaborador ${staff.id}`
    );
};


const loungeLabel = (resource, lounges=[]) => {
    const lounge = resource && resource.lounge;

    if (!lounge)
        return '';

    if (typeof lounge === 'object' && lounge.name)
        return lounge.name;

    const loungeId = relationId(lounge);

    const matched = lounges.find(
        item => String(item.id) === String(loungeId)
    );

    return matched && matched.name
        ? matched.name
        : loungeId;
};


const statusLabels = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En proceso',
    FINISHED: 'Finalizada',
    CANCELED: 'Cancelada'
};


const SpaAppointmentDetailModal = ({
    show,
    appointment,
    onHide,
    onUpdated
}) => {

    const api = useContext(ApiContext);

    const [resources, setResources] = useState([]);
    const [staffs, setStaffs] = useState([]);
    const [lounges, setLounges] = useState([]);

    const [resourceId, setResourceId] = useState('');
    const [staffId, setStaffId] = useState('');
    const [date, setDate] = useState('');
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState('');
    const [comments, setComments] = useState('');

    const [loading, setLoading] = useState(false);
    const [loadingSlots, setLoadingSlots] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');


    const editable = !!(
        appointment &&
        appointment.status === 'PENDING'
    );


    const service = appointment && appointment.service
        ? appointment.service
        : {};


    const requiresStaff = !!(
        service.requiresStaff === true ||
        service.requiresStaff === 1 ||
        service.requiresStaff === '1'
    );


    const serviceResourceType = relationId(
        service.resourceType
    );


    const selectedResource = useMemo(
        () => resources.find(
            resource =>
                String(resource.id) === String(resourceId)
        ),
        [resources, resourceId]
    );


    const compatibleResources = useMemo(
        () => resources.filter(resource => {
            const active =
                resource.active === true ||
                resource.active === 1 ||
                resource.active === '1';

            if (!active)
                return false;

            if (
                resource.status &&
                resource.status !== 'AVAILABLE' &&
                String(resource.id) !== String(resourceId)
            )
                return false;

            if (!serviceResourceType)
                return true;

            return (
                relationId(resource.resourceType) ===
                serviceResourceType
            );
        }),
        [resources, serviceResourceType, resourceId]
    );


    const activeStaffs = useMemo(
        () => staffs.filter(staff => (
            staff.active === true ||
            staff.active === 1 ||
            staff.active === '1' ||
            String(staff.id) === String(staffId)
        )),
        [staffs, staffId]
    );


    useEffect(() => {
        if (!show || !appointment)
            return;

        setResourceId(relationId(appointment.resource));
        setStaffId(relationId(appointment.staff));
        setDate(datePart(appointment.scheduledStartDate));
        setSelectedSlot(timePart(appointment.scheduledStartDate));
        setComments(appointment.comments || '');
        setSlots([]);
        setError('');

        if (!editable)
            return;

        setLoading(true);

        Promise.all([
            api.spaResources.get({
                filters: {
                    pagination: false,
                    'order[name]': 'ASC'
                },
                customProp: '_spaDetailResources'
            }),
            api.spaStaffs.get({
                filters: {
                    pagination: false
                },
                customProp: '_spaDetailStaffs'
            }),
            api.lounges.get2({
                filters: {
                    isLounge: true
                },
                customProp: '_spaDetailLounges'
            })
        ])
            .then(responses => {
                setResources(collection(responses[0]));
                setStaffs(collection(responses[1]));
                setLounges(collection(responses[2]));
            })
            .catch(() =>
                setError(
                    'No fue posible cargar los datos para editar la cita.'
                )
            )
            .finally(() =>
                setLoading(false)
            );

    }, [show, appointment, editable, api]);


    useEffect(() => {
        if (
            !show ||
            !editable ||
            !selectedResource ||
            !date
        )
            return;

        if (requiresStaff && !staffId)
            return;

        const loungeId = relationId(selectedResource.lounge);

        if (!loungeId)
            return;

        const filters = {
            service: service.id,
            lounge: loungeId,
            resource: selectedResource.id,
            date
        };

        if (requiresStaff)
            filters.staff = staffId;

        setLoadingSlots(true);
        setError('');

        api.spaAvailability.get({
            filters,
            customProp: '_spaDetailAvailability'
        })
            .then(response => {
                const result = data(response);

                const availableSlots =
                    Array.isArray(result.slots)
                        ? result.slots.filter(
                            slot => slot.available === true
                        )
                        : [];

                setSlots(availableSlots);
            })
            .catch(() =>
                setError(
                    'No fue posible consultar la disponibilidad.'
                )
            )
            .finally(() =>
                setLoadingSlots(false)
            );

    }, [
        show,
        editable,
        api,
        selectedResource,
        staffId,
        date,
        requiresStaff,
        service.id
    ]);


    const canSave =
        editable &&
        !!resourceId &&
        !!date &&
        !!selectedSlot &&
        (!requiresStaff || !!staffId) &&
        !saving;


    const save = () => {
        if (!canSave || !selectedResource)
            return;

        const loungeId = relationId(selectedResource.lounge);

        const scheduledStartDate =
            appointmentDateTime(selectedSlot, date);

        const payload = {
            loungeId,
            resourceId,
            scheduledStartDate,
            comments: comments.trim() || null
        };

        if (requiresStaff)
            payload.staffId = staffId;
        else
            payload.staffId = null;

        setSaving(true);
        setError('');

        api.spaAppointments.reschedule({
            id: appointment.id,
            appointment: payload,
            customProp: '_spaAppointmentReschedule',
            loadingId: 'SpaAppointmentDetailModal.reschedule'
        })
            .then(() => {
                if (onUpdated)
                    return onUpdated();

                return null;
            })
            .catch(() =>
                setError(
                    'No fue posible guardar los cambios. '
                    + 'La disponibilidad pudo haber cambiado.'
                )
            )
            .finally(() =>
                setSaving(false)
            );
    };


    if (!appointment)
        return null;


    const guestName =
        appointment.guest && appointment.guest.name
            ? appointment.guest.name
            : 'Huésped';

    const actualDurationMinutes = (() => {
        if (!appointment.startedAt || !appointment.finishedAt)
            return null;

        const started = new Date(appointment.startedAt);
        const finished = new Date(appointment.finishedAt);

        if (
            Number.isNaN(started.getTime()) ||
            Number.isNaN(finished.getTime())
        )
            return null;

        return Math.max(
            0,
            Math.round(
                (finished.getTime() - started.getTime()) / 60000
            )
        );
    })();


    return (
        <Modal
            show={show}
            onHide={saving ? undefined : onHide}
            bsSize="large"
        >
            <Modal.Header closeButton={!saving}>
                <Modal.Title>
                    <i
                        className="fa fa-calendar-check-o"
                        style={{
                            marginRight: 9,
                            color: "#337ab7"
                        }}
                    />
                    Detalle de cita SPA
                </Modal.Title>
            </Modal.Header>

            <Modal.Body>

                {error &&
                    <Alert bsStyle="danger">
                        {error}
                    </Alert>
                }

                <div
                    style={{
                        background: "#f7faff",
                        border: "1px solid #e1e5ea",
                        borderRadius: 4,
                        padding: "12px 14px",
                        marginBottom: 18
                    }}
                >
                    <strong>Información de la cita</strong>

                    <span
                        className="pull-right"
                        style={{fontWeight: 600}}
                    >
                        {statusLabels[appointment.status] ||
                            appointment.status}
                    </span>
                </div>

                {loading &&
                    <Alert bsStyle="info">
                        Cargando información de la cita...
                    </Alert>
                }

                <Row>
                    <Col md={8}>
                        <FormGroup>
                            <ControlLabel>Huésped</ControlLabel>
                            <FormControl
                                type="text"
                                value={guestName}
                                disabled
                            />
                        </FormGroup>
                    </Col>

                    <Col md={4}>
                        <FormGroup>
                            <ControlLabel>Folio</ControlLabel>
                            <FormControl
                                type="text"
                                value={appointment.folio || ''}
                                disabled
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>Sala</ControlLabel>
                            <FormControl
                                type="text"
                                value={
                                    appointment.lounge &&
                                    appointment.lounge.name
                                        ? appointment.lounge.name
                                        : ''
                                }
                                disabled
                            />
                        </FormGroup>
                    </Col>

                    <Col md={6}>
                        <FormGroup>
                            <ControlLabel>Asiento</ControlLabel>
                            <FormControl
                                type="text"
                                value={appointment.seat || 'Sin asignar'}
                                disabled
                            />
                        </FormGroup>
                    </Col>
                </Row>

                <Row>
                    <Col md={12}>
                        <FormGroup>
                            <ControlLabel>Servicio</ControlLabel>
                            <FormControl
                                type="text"
                                value={service.name || ''}
                                disabled
                            />
                        </FormGroup>
                    </Col>
                </Row>

                {editable ? (
                    <div>
                        {requiresStaff &&
                            <Row>
                                <Col md={12}>
                                    <FormGroup>
                                        <ControlLabel>
                                            Cosmetólog(a)
                                        </ControlLabel>

                                        <FormControl
                                            componentClass="select"
                                            value={staffId}
                                            onChange={e => {
                                                setStaffId(e.target.value);
                                                setSelectedSlot('');
                                                setSlots([]);
                                            }}
                                        >
                                            <option value="">
                                                Selecciona personal...
                                            </option>

                                            {activeStaffs.map(staff =>
                                                <option
                                                    key={staff.id}
                                                    value={staff.id}
                                                >
                                                    {staffLabel(staff)}
                                                </option>
                                            )}
                                        </FormControl>
                                    </FormGroup>
                                </Col>
                            </Row>
                        }

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>Fecha</ControlLabel>

                                    <FormControl
                                        type="date"
                                        value={date}
                                        onChange={e => {
                                            setDate(e.target.value);
                                            setSelectedSlot('');
                                            setSlots([]);
                                        }}
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>
                                        Horario disponible
                                    </ControlLabel>

                                    <FormControl
                                        componentClass="select"
                                        value={slotValue(selectedSlot)}
                                        onChange={e => {
                                            const found = slots.find(
                                                slot =>
                                                    slotValue(slot) ===
                                                    e.target.value
                                            );

                                            setSelectedSlot(
                                                found || e.target.value
                                            );
                                        }}
                                        disabled={
                                            !selectedResource ||
                                            loadingSlots ||
                                            (
                                                requiresStaff &&
                                                !staffId
                                            )
                                        }
                                    >
                                        <option value="">
                                            {loadingSlots
                                                ? 'Consultando...'
                                                : 'Selecciona un horario...'}
                                        </option>

                                        {slots.map((slot, index) =>
                                            <option
                                                key={
                                                    `${slotValue(slot)}-${index}`
                                                }
                                                value={slotValue(slot)}
                                            >
                                                {slotLabel(slot)}
                                            </option>
                                        )}
                                    </FormControl>
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={12}>
                                <FormGroup>
                                    <ControlLabel>
                                        Comentarios
                                    </ControlLabel>

                                    <FormControl
                                        componentClass="textarea"
                                        rows={3}
                                        value={comments}
                                        onChange={e =>
                                            setComments(e.target.value)
                                        }
                                        placeholder="Opcional"
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                ) : (
                    <div>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Fecha</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={date}
                                        disabled
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Hora programada</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={
                                            timePart(
                                                appointment.scheduledStartDate
                                            )
                                        }
                                        disabled
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={4}>
                                <FormGroup>
                                    <ControlLabel>Hora inicio real</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={
                                            appointment.startedAt
                                                ? timePart(appointment.startedAt)
                                                : 'Pendiente'
                                        }
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>Hora final real</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={
                                            appointment.finishedAt
                                                ? timePart(appointment.finishedAt)
                                                : 'Pendiente'
                                        }
                                        disabled
                                    />
                                </FormGroup>
                            </Col>

                            <Col md={6}>
                                <FormGroup>
                                    <ControlLabel>
                                        Tiempo de atención
                                    </ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={
                                            actualDurationMinutes !== null
                                                ? `${actualDurationMinutes} min`
                                                : 'Pendiente'
                                        }
                                        disabled
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        {appointment.comments &&
                            <FormGroup>
                                <ControlLabel>Comentarios</ControlLabel>
                                <FormControl
                                    componentClass="textarea"
                                    rows={3}
                                    value={appointment.comments}
                                    disabled
                                />
                            </FormGroup>
                        }

                        {appointment.status === 'CANCELED' &&
                            <Alert bsStyle="danger">
                                <strong>
                                    Motivo de cancelación
                                </strong>
                                <div style={{marginTop: 5}}>
                                    {appointment.cancellationReason ||
                                        'Sin motivo registrado.'}
                                </div>
                            </Alert>
                        }
                    </div>
                )}

            </Modal.Body>

            <Modal.Footer>
                <Button
                    onClick={onHide}
                    disabled={saving}
                >
                    Cerrar
                </Button>

                {editable &&
                    <Button
                        bsStyle="primary"
                        onClick={save}
                        disabled={!canSave}
                    >
                        <i className="fa fa-save"/>
                        {' '}
                        {saving
                            ? 'Guardando...'
                            : 'Guardar cambios'}
                    </Button>
                }
            </Modal.Footer>
        </Modal>
    );
};


export default SpaAppointmentDetailModal;
